import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTheme } from "../../../../contexts/ThemeContext.js";
import { useAuth } from "../../../../contexts/AuthContext.js";
import { useTenancy } from "../../../../contexts/TenancyContext.js";
import { getFirestore, doc, getDoc, setDoc } from 'firebase/firestore';
import { deiterateAPI } from "../../../../js/external-js/variableModules/utils/deiterateAPI.util";
import documentsService from "../../../../services/documentsService";
import slackIcon from '../../../../assets/img/slack.png';
import heroBackground from '@/assets/img/imagebg.png';
import { getAvailableFeatures } from "@/utils/featuresUtil";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { Sparkles, Plug, ShieldCheck, Server, Settings2, Loader2, ExternalLink, Power, BookOpen, ChevronDown, Settings, Info } from "lucide-react";
import { ExecutionHistory } from "@/components/integrations/ExecutionHistory";
import { LogViewer } from "@/components/integrations/LogViewer";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface AtlassianIntegration {
  atlassianAPIKey?: {
    value?: string;
    cloudSecretVersionName?: string;
  };
  atlassianURL?: string;
  atlassianUserEmail?: string;
}

interface IntegrationArtifact {
  storagePath?: string;
  bucket?: string;
  url?: string;
  urlExpiresAt?: string;
  size?: number;
  checksum?: string;
}

interface IntegrationRuntime {
  language?: string;
  entryFile?: string;
  handler?: string;
}

interface IntegrationRecord {
  id: string;
  name: string;
  description?: string;
  type?: string;
  version?: string;
  artifact?: IntegrationArtifact | null;
  runtime?: IntegrationRuntime | null;
  triggers?: any[];
  configSchema?: Record<string, any> | null;
  config?: Record<string, any> | null;
  enabled?: boolean;
  tenantIntegrationId?: string | null;
  tenantIntegration?: Record<string, any> | null;
  definition?: Record<string, any> | null;
}

type SchemaFieldType = 'string' | 'number' | 'integer' | 'boolean' | 'array';

interface SchemaField {
  key: string;
  label: string;
  description?: string;
  required: boolean;
  enum?: string[];
  type: SchemaFieldType;
  format?: string;
  defaultValue?: any;
}

interface SharePointAuthStatus {
  isAuthenticated: boolean;
  userEmail?: string;
  tenantId?: string;
  expiresAt?: string;
  sharePointUser?: {
    displayName: string;
    mail: string;
    userPrincipalName: string;
    id: string;
    jobTitle?: string;
    officeLocation?: string;
    businessPhones?: string[];
    mobilePhone?: string;
  };
  tenant?: {
    domain: string;
    tenantId: string;
  };
}

interface SharePointSite {
  id: string;
  displayName: string;
  webUrl: string;
  drives: SharePointDrive[];
}

interface SharePointDrive {
  id: string;
  name: string;
  driveType: string;
  webUrl: string;
}

interface SharePointFile {
  id: string;
  name: string;
  size: number;
  downloadUrl: string;
  webUrl: string;
  lastModifiedDateTime: string;
  createdDateTime: string;
  mimeType: string;
  parentReference: {
    driveId: string;
    id: string;
    path: string;
  };
}

interface IntegrationsData {
  atlassian?: AtlassianIntegration;
}

export default function IntegrationsSection() {
  const { isDark } = useTheme();
  const { user } = useAuth();
  const { activeTenancy } = useTenancy();

  const availableFeatures = getAvailableFeatures();
  const hasSharePoint = !!availableFeatures.find(f => f.name === 'SharePoint');

  const [integrations, setIntegrations] = useState<IntegrationsData>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    atlassianAPIKey: '',
    atlassianURL: '',
    atlassianUserEmail: ''
  });
  const [marketplaceLoading, setMarketplaceLoading] = useState(false);
  const [marketplaceError, setMarketplaceError] = useState<string | null>(null);
  const [integrationRecords, setIntegrationRecords] = useState<IntegrationRecord[]>([]);
  const [selectedIntegration, setSelectedIntegration] = useState<IntegrationRecord | null>(null);
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [configTextareaValue, setConfigTextareaValue] = useState("");
  const [configError, setConfigError] = useState<string | null>(null);
  const [configSaving, setConfigSaving] = useState(false);
  const [configSchemaFields, setConfigSchemaFields] = useState<SchemaField[]>([]);
  const [configFormValues, setConfigFormValues] = useState<Record<string, any>>({});
  const [configUsesSchema, setConfigUsesSchema] = useState(false);
  const [setupInstructions, setSetupInstructions] = useState<any>(null);
  const [actionIntegrationId, setActionIntegrationId] = useState<string | null>(null);
  const [selectedLogEvents, setSelectedLogEvents] = useState<Record<string, string>>({});
  const [integrationActiveTabs, setIntegrationActiveTabs] = useState<Record<string, string>>({});
  const [catalogDialogOpen, setCatalogDialogOpen] = useState(false);
  const [catalogSearch, setCatalogSearch] = useState('');
  const [catalogHostingFilter, setCatalogHostingFilter] = useState<'all' | 'hosted' | 'self'>('all');
  const [catalogStatusFilter, setCatalogStatusFilter] = useState<'all' | 'enabled' | 'available'>('all');
  const [confluenceDialogOpen, setConfluenceDialogOpen] = useState(false);
  const [sharePointDialogOpen, setSharePointDialogOpen] = useState(false);

  // SharePoint integration state
  const [sharePointAuthStatus, setSharePointAuthStatus] = useState<SharePointAuthStatus>({ isAuthenticated: false });
  const [sharePointSites, setSharePointSites] = useState<SharePointSite[]>([]);
  const [sharePointFiles, setSharePointFiles] = useState<SharePointFile[]>([]);
  const [selectedSite, setSelectedSite] = useState<SharePointSite | null>(null);
  const [selectedDrive, setSelectedDrive] = useState<SharePointDrive | null>(null);
  const [isSharePointLoading, setIsSharePointLoading] = useState(false);
  const [sharePointError, setSharePointError] = useState<string | null>(null);
  const [sharePointTenantId, setSharePointTenantId] = useState('');
  const [testResults, setTestResults] = useState<string[]>([]);

  const db = getFirestore();

  const tenancyId = activeTenancy?.id || (window as any).companyID;

  const fetchMarketplaceIntegrations = useCallback(async () => {
    if (!tenancyId) return;
    setMarketplaceLoading(true);
    setMarketplaceError(null);
    try {
      const response = await deiterateAPI.GET<{ integrations?: IntegrationRecord[]; statusCode?: number }>(
        `/v1/integrations`,
        {
          backend: 'thalassa',
          headers: {
            'x-tenancy-id': tenancyId,
            'company-id': tenancyId,
            'customer-id': tenancyId,
          },
        }
      );
      setIntegrationRecords(response?.integrations ?? []);
    } catch (error: any) {
      console.error('[IntegrationsSection] Failed to load integrations marketplace', error);
      setMarketplaceError(error?.message || 'Unable to load integrations marketplace.');
    } finally {
      setMarketplaceLoading(false);
    }
  }, [tenancyId]);

  useEffect(() => {
    fetchMarketplaceIntegrations();
  }, [fetchMarketplaceIntegrations]);

  const refreshMarketplace = () => fetchMarketplaceIntegrations();

  const handleEnableIntegration = async (integrationId: string, config?: Record<string, any> | null) => {
    if (!tenancyId) {
      toast.error('No tenancy ID available');
      return;
    }
    setActionIntegrationId(integrationId);
    setMarketplaceError(null);
    try {
      await deiterateAPI.POST(
        `/v1/integrations/${integrationId}/enable`,
        {
          backend: 'thalassa',
          headers: {
            'x-tenancy-id': tenancyId,
            'company-id': tenancyId,
            'customer-id': tenancyId,
          },
          body: JSON.stringify({
            enabled: true,
            config: config ?? undefined,
          }),
        }
      );
      toast.success('Integration enabled');
      await fetchMarketplaceIntegrations();
    } catch (error: any) {
      console.error('[IntegrationsSection] Failed to enable integration', error);
      toast.error(error?.message || 'Failed to enable integration');
    } finally {
      setActionIntegrationId(null);
    }
  };

  const handleDisableIntegration = async (integrationId: string) => {
    if (!tenancyId) {
      toast.error('No tenancy ID available');
      return;
    }
    setActionIntegrationId(integrationId);
    setMarketplaceError(null);
    try {
      await deiterateAPI.POST(
        `/v1/integrations/${integrationId}/disable`,
        {
          backend: 'thalassa',
          headers: {
            'x-tenancy-id': tenancyId,
            'company-id': tenancyId,
            'customer-id': tenancyId,
          },
        }
      );
      toast.success('Integration disabled');
      await fetchMarketplaceIntegrations();
    } catch (error: any) {
      console.error('[IntegrationsSection] Failed to disable integration', error);
      toast.error(error?.message || 'Failed to disable integration');
    } finally {
      setActionIntegrationId(null);
    }
  };

  const handleManualTrigger = async (integration: IntegrationRecord) => {
    if (!tenancyId) {
      toast.error('No tenancy ID available');
      return;
    }
    setActionIntegrationId(integration.id);
    setMarketplaceError(null);
    try {
      // Trigger the integration using the /trigger endpoint
      // deiterateAPI now automatically handles JSON serialization
      const response = await deiterateAPI.POST(
        `/v1/integrations/${integration.id}/trigger`,
        {
          backend: 'thalassa',
          headers: {
            'x-tenancy-id': tenancyId,
            'company-id': tenancyId,
            'customer-id': tenancyId,
          },
          body: {
            payload: {},
          },
        }
      );
      
      if (response?.statusCode === 200) {
        toast.success(`Integration "${integration.name}" triggered successfully`);
      } else {
        throw new Error(response?.error || 'Failed to trigger integration');
      }
    } catch (error: any) {
      console.error('[IntegrationsSection] Failed to trigger integration', error);
      toast.error(error?.message || 'Failed to trigger integration');
    } finally {
      setActionIntegrationId(null);
    }
  };

  const handleConfigureIntegration = (integration: IntegrationRecord) => {
    openConfigModal(integration);
  };

  const openConfigModal = (integration: IntegrationRecord) => {
    setSelectedIntegration(integration);
    console.log('[IntegrationsSection] Opening config for:', integration.id);
    console.log('[IntegrationsSection] Raw configSchema:', integration.configSchema);
    const resolvedSchema = resolveConfigSchema(integration);
    console.log('[IntegrationsSection] Resolved schema:', resolvedSchema);
    const schemaFields = buildSchemaFields(resolvedSchema);
    console.log('[IntegrationsSection] Built fields:', schemaFields);
    console.log('[IntegrationsSection] schemaFields.length:', schemaFields.length);
    setConfigSchemaFields(schemaFields);

    // Extract setup instructions from the config schema
    const instructions = integration.configSchema?.setupInstructions || integration.definition?.setupInstructions || null;
    setSetupInstructions(instructions);

    console.log('[IntegrationsSection] Setting configUsesSchema to:', schemaFields.length > 0);
    if (schemaFields.length > 0) {
      const initialValues: Record<string, any> = {};
      schemaFields.forEach((field) => {
        if (integration.config && field.key in (integration.config || {})) {
          initialValues[field.key] = integration.config?.[field.key];
        } else if (field.defaultValue !== undefined) {
          initialValues[field.key] = field.defaultValue;
        } else if (field.type === 'boolean') {
          initialValues[field.key] = false;
        } else if (field.type === 'array') {
          initialValues[field.key] = [];
        } else {
          initialValues[field.key] = '';
        }
      });
      setConfigFormValues(initialValues);
      setConfigUsesSchema(true);
    } else {
      setConfigUsesSchema(false);
    }
    setConfigTextareaValue(JSON.stringify(integration.config || {}, null, 2));
    setConfigModalOpen(true);
  };

  const closeConfigModal = () => {
    if (configSaving) return;
    setConfigModalOpen(false);
    setSelectedIntegration(null);
    setConfigTextareaValue('');
    setConfigError(null);
    setConfigSchemaFields([]);
    setConfigFormValues({});
    setConfigUsesSchema(false);
    setSetupInstructions(null);
  };

  const handleSaveConfig = async () => {
    if (!selectedIntegration) return;
    setConfigError(null);
    let payload: Record<string, any> | null = null;

    if (configUsesSchema && configSchemaFields.length > 0) {
      const missingRequired: string[] = [];
      const computed: Record<string, any> = {};
      configSchemaFields.forEach((field) => {
        const value = configFormValues[field.key];
        const isEmptyArray = Array.isArray(value) && value.length === 0;
        const isEmptyString = value === '' || value === undefined || value === null;
        if (field.required && (isEmptyString || isEmptyArray)) {
          missingRequired.push(field.label);
          return;
        }
        if (!isEmptyString && !isEmptyArray) {
          computed[field.key] = value;
        }
      });
      if (missingRequired.length > 0) {
        setConfigError(`Please fill required fields: ${missingRequired.join(', ')}`);
        return;
      }
      payload = Object.keys(computed).length ? computed : null;
    } else {
      try {
        payload = configTextareaValue.trim() ? JSON.parse(configTextareaValue) : null;
      } catch (error: any) {
        setConfigError('Invalid JSON: ' + error.message);
        return;
      }
    }

    setConfigSaving(true);
    try {
      await handleEnableIntegration(selectedIntegration.id, payload || undefined);
      closeConfigModal();
    } catch {
      // handleEnableIntegration already toasts
    } finally {
      setConfigSaving(false);
    }
  };

  const sortedIntegrations = useMemo(() => {
    return [...integrationRecords].sort((a, b) => {
      const enabledDelta = Number(!!b.enabled) - Number(!!a.enabled);
      if (enabledDelta !== 0) return enabledDelta;
      return a.name.localeCompare(b.name);
    });
  }, [integrationRecords]);

  const enabledIntegrations = useMemo(
    () => integrationRecords.filter((integration) => integration.enabled),
    [integrationRecords]
  );

  const getHostingLabel = useCallback(
    (integration: IntegrationRecord) => {
      const hosting =
        integration.definition?.hosting ||
        (integration.definition?.selfHosted ? 'Self Hosted' : undefined) ||
        (integration.definition?.managed ? 'Managed' : undefined);
      if (hosting) return hosting;
      return integration.artifact ? 'de.iterate Hosted' : 'Self Managed';
    },
    []
  );

  const getPublisherLabel = useCallback(
    (integration: IntegrationRecord) => {
      return (
        integration.definition?.publisher ||
        integration.definition?.author ||
        integration.definition?.vendor ||
        'de.iterate'
      );
    },
    []
  );

  const resolveConfigSchema = useCallback((integration: IntegrationRecord | null | undefined) => {
    if (!integration) return null;
    
    // Try to get the schema from various locations
    let schema = 
      integration.configSchema ||
      integration.definition?.config ||
      integration.definition?.configSchema ||
      null;
    
    // Handle nested configSchema (e.g., configSchema.configSchema)
    // Keep unwrapping while we find nested configSchema properties
    while (schema && typeof schema === 'object' && 'configSchema' in schema && schema.configSchema !== schema) {
      schema = schema.configSchema;
    }
    
    return schema;
  }, []);

  const toLabel = (key: string) =>
    key
      .replace(/[_-]+/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/\b\w/g, (char) => char.toUpperCase());

  const buildSchemaFields = useCallback((schema?: Record<string, any> | null): SchemaField[] => {
    if (!schema || typeof schema !== 'object') {
      console.log('[buildSchemaFields] Invalid schema:', schema);
      return [];
    }
    
    // Handle both standard JSON Schema (with properties) and legacy format (fields at top level)
    let properties = schema.properties;
    
    console.log('[buildSchemaFields] Initial properties:', properties);
    
    // If no properties but we have fields at the top level with type/description, use those
    if (!properties || Object.keys(properties).length === 0) {
      properties = {};
      Object.keys(schema).forEach(key => {
        const value = schema[key];
        if (value && typeof value === 'object' && ('type' in value || 'description' in value)) {
          properties[key] = value;
        }
      });
      console.log('[buildSchemaFields] Extracted properties from top level:', properties);
    }
    
    if (!properties || Object.keys(properties).length === 0) {
      console.log('[buildSchemaFields] No properties found in schema');
      return [];
    }
    
    const requiredKeys = new Set(schema.required ?? []);
    const fields = Object.keys(properties).map((key) => {
      const prop = properties[key] || {};
      const fieldType = Array.isArray(prop.type) ? prop.type[0] : prop.type;
      let normalizedType: SchemaFieldType = 'string';
      if (fieldType === 'integer') normalizedType = 'integer';
      else if (fieldType === 'number') normalizedType = 'number';
      else if (fieldType === 'boolean') normalizedType = 'boolean';
      else if (fieldType === 'array') normalizedType = 'array';
      else if (fieldType === 'secret') normalizedType = 'string'; // Treat secret as string
      else normalizedType = 'string';
      
      return {
        key,
        label: prop.title || prop.label || toLabel(key),
        description: prop.description,
        required: prop.required ?? requiredKeys.has(key),
        enum: prop.enum,
        type: normalizedType,
        format: prop.format || (fieldType === 'secret' ? 'password' : undefined),
        defaultValue: prop.default,
      };
    });
    
    console.log('[buildSchemaFields] Built fields:', fields);
    return fields;
  }, []);

  const getFieldDisplayValue = useCallback(
    (field: SchemaField) => {
      const value = configFormValues[field.key];
      if (field.type === 'array') {
        if (Array.isArray(value)) return value.join('\n');
        if (typeof value === 'string') return value;
        return '';
      }
      if (field.type === 'boolean') {
        return Boolean(value);
      }
      return value === undefined || value === null ? '' : value;
    },
    [configFormValues]
  );

  const setFieldValue = (field: SchemaField, rawValue: any) => {
    let nextValue: any = rawValue;
    switch (field.type) {
      case 'number':
        nextValue = rawValue === '' ? '' : Number(rawValue);
        break;
      case 'integer':
        nextValue = rawValue === '' ? '' : parseInt(rawValue, 10);
        break;
      case 'boolean':
        nextValue = Boolean(rawValue);
        break;
      case 'array':
        if (typeof rawValue === 'string') {
          nextValue = rawValue
            .split(/[\n,]+/)
            .map((item) => item.trim())
            .filter((item) => item.length > 0);
        }
        break;
      default:
        nextValue = rawValue;
    }
    setConfigFormValues((prev) => ({
      ...prev,
      [field.key]: nextValue,
    }));
  };

  const getTriggerSummary = useCallback((integration: IntegrationRecord) => {
    const triggers = integration.triggers;
    if (!triggers || triggers.length === 0) {
      return 'Manual run';
    }
    if (triggers.length === 1) {
      const trigger = triggers[0] as any;
      if (trigger?.description) {
        return trigger.description;
      }
      if (trigger?.type === 'schedule') {
        const cron = trigger?.schedule?.cron;
        const timezone = trigger?.schedule?.timezone;
        if (cron) {
          return `Cron ${cron}${timezone ? ` (${timezone})` : ''}`;
        }
        return 'Scheduled trigger';
      }
      if (trigger?.type === 'event') {
        const source = trigger?.event?.type || trigger?.id || 'Event';
        return `Event trigger • ${source}`;
      }
      return `${trigger?.type ?? 'Custom'} trigger`;
    }
    return `${triggers.length} published triggers`;
  }, []);

  const filteredCatalogIntegrations = useMemo(() => {
    const query = catalogSearch.trim().toLowerCase();
    return sortedIntegrations.filter((integration) => {
      const text = `${integration.name} ${integration.description ?? ''}`.toLowerCase();
      const matchesSearch = !query || text.includes(query);
      const hostingLabel = getHostingLabel(integration).toLowerCase();
      const matchesHosting =
        catalogHostingFilter === 'all' ||
        (catalogHostingFilter === 'hosted' && hostingLabel.includes('hosted')) ||
        (catalogHostingFilter === 'self' && hostingLabel.includes('self'));
      const matchesStatus =
        catalogStatusFilter === 'all' ||
        (catalogStatusFilter === 'enabled' && integration.enabled) ||
        (catalogStatusFilter === 'available' && !integration.enabled);
      return matchesSearch && matchesHosting && matchesStatus;
    });
  }, [catalogHostingFilter, catalogSearch, catalogStatusFilter, getHostingLabel, sortedIntegrations]);

  const marketplaceStats = useMemo(() => {
    const enabled = integrationRecords.filter((integration) => integration.enabled).length;
    return {
      total: integrationRecords.length,
      enabled,
      available: Math.max(integrationRecords.length - enabled, 0),
    };
  }, [integrationRecords]);

  const getRuntimeSummary = (integration: IntegrationRecord) => {
    if (!integration.runtime) return null;
    const language = integration.runtime.language?.toUpperCase() || 'JS';
    const entry = integration.runtime.entryFile || 'index.js';
    const handler = integration.runtime.handler || 'run';
    return `${language} • ${entry} → ${handler}`;
  };

  // Helper function to encrypt data using RSA with Web Crypto API
  const encryptWithRSA = async (publicKeyPem: string, data: string): Promise<ArrayBuffer> => {
    try {
      // Convert PEM to binary
      const pemHeader = "-----BEGIN PUBLIC KEY-----";
      const pemFooter = "-----END PUBLIC KEY-----";
      const pemContents = publicKeyPem
        .replace(pemHeader, "")
        .replace(pemFooter, "")
        .replace(/\s/g, "");

      // Convert from base64 to binary
      const binaryDer = Uint8Array.from(atob(pemContents), c => c.charCodeAt(0));

      // Import the key
      const publicKey = await crypto.subtle.importKey(
        "spki",
        binaryDer,
        {
          name: "RSA-OAEP",
          hash: "SHA-256",
        },
        false,
        ["encrypt"]
      );

      // Encrypt the data
      const encoded = new TextEncoder().encode(data);
      const encrypted = await crypto.subtle.encrypt(
        {
          name: "RSA-OAEP",
        },
        publicKey,
        encoded
      );

      return encrypted;
    } catch (error) {
      console.error('Error encrypting with RSA:', error);
      throw new Error('Failed to encrypt data with RSA');
    }
  };

  // Load integrations data from Firestore and Google Cloud Secrets
  useEffect(() => {
    const loadIntegrationsData = async () => {
      if (!activeTenancy?.id || !user) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);

        // Fetch integrations document from Firestore
        const integrationsRef = doc(db, 'customers', activeTenancy.id, 'companySettings', 'integrations');
        const integrationsDoc = await getDoc(integrationsRef);

        if (integrationsDoc.exists()) {
          const data = integrationsDoc.data() as IntegrationsData;
          setIntegrations(data);

          // Populate form with existing data
          const atlassian = data.atlassian || {};
          setFormData({
            atlassianURL: atlassian.atlassianURL || '',
            atlassianUserEmail: atlassian.atlassianUserEmail || '',
            atlassianAPIKey: '' // Don't pre-populate for security
          });

          // If there's an API key, try to fetch it from Google Cloud Secrets
          if (atlassian.atlassianAPIKey?.cloudSecretVersionName) {
            try {
              const secretData = await deiterateAPI.POST('/deiterateInternalAPI/settings/getAPISecret', {
                body: JSON.stringify({
                  customerID: activeTenancy.id,
                  secretKey: 'atlassian.atlassianAPIKey'
                }),
                companyID: activeTenancy.id
              });

              if (secretData?.secret?.value) {
                setFormData(prev => ({ ...prev, atlassianAPIKey: secretData.secret.value }));
              }
            } catch (error) {
              console.log('ℹ️ No existing API key found in secrets (this is normal for new setups):', error.message || error);
              // Don't throw - this is expected for new integrations
            }
          }
        }
      } catch (error) {
        console.error('Error loading integrations data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadIntegrationsData();
  }, [activeTenancy?.id, user, db]);

  // Handle form input changes
  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Save integration data
  const handleLegacyConfluenceSave = async () => {
    if (!activeTenancy?.id || !user) return;

    try {
      setIsSaving(true);

      const updatedIntegrations: IntegrationsData = {
        ...integrations,
        atlassian: {
          ...integrations.atlassian,
          atlassianURL: formData.atlassianURL,
          atlassianUserEmail: formData.atlassianUserEmail,
          // Only update API key if a new one was provided
          ...(formData.atlassianAPIKey ? {
            atlassianAPIKey: {
              ...integrations.atlassian?.atlassianAPIKey,
              value: formData.atlassianAPIKey
            }
          } : {})
        }
      };

      // Save to Firestore
      const integrationsRef = doc(db, 'customers', activeTenancy.id, 'companySettings', 'integrations');
      await setDoc(integrationsRef, updatedIntegrations, { merge: true });

      // If API key was updated, encrypt and save to Google Cloud Secrets
      if (formData.atlassianAPIKey) {
        try {
          // Use native Web Crypto API for encryption (React-compatible)
          const publicKeyPem = (window as any).publicKey || import.meta.env.VITE_publicKey;

          console.log('🔍 Public key check:', {
            windowPublicKey: !!(window as any).publicKey,
            envPublicKey: !!import.meta.env.VITE_publicKey,
            publicKeyLength: publicKeyPem ? publicKeyPem.length : 0
          });

          if (!publicKeyPem || publicKeyPem.length < 100) {
            console.warn('⚠️ Public key not available or invalid. API key will be saved without encryption.');

            // Fallback: Save unencrypted (for development/testing)
            const dataToSave = {
              atlassian: {
                atlassianAPIKey: {
                  value: formData.atlassianAPIKey // Temporary: unencrypted for dev
                },
                ...(formData.atlassianURL && { atlassianURL: { value: formData.atlassianURL } }),
                ...(formData.atlassianUserEmail && { atlassianUserEmail: { value: formData.atlassianUserEmail } })
              }
            };

            const result = await deiterateAPI.POST('/deiterateInternalAPI/settings/createAPISecret', {
              body: JSON.stringify({
                customerID: activeTenancy.id,
                ...dataToSave
              }),
              companyID: activeTenancy.id
            });

            console.log('✅ Integration saved successfully (unencrypted mode):', result);
            return;
          }

          // Encrypt using native Web Crypto API
          const encryptedBuffer = await encryptWithRSA(publicKeyPem, formData.atlassianAPIKey);

          // Convert ArrayBuffer to base64 string for storage
          const encryptedAPIKey = btoa(String.fromCharCode(...new Uint8Array(encryptedBuffer)));

          console.log('🔐 Encryption successful:', {
            originalLength: formData.atlassianAPIKey.length,
            encryptedLength: encryptedAPIKey.length,
            encryptedPreview: encryptedAPIKey.substring(0, 50) + '...'
          });

          // Use createAPISecret endpoint with encrypted data structure
          const dataToSave = {
            atlassian: {
              atlassianAPIKey: {
                value: encryptedAPIKey,
                encrypted: true // Flag to indicate this is encrypted
              },
              ...(formData.atlassianURL && { atlassianURL: { value: formData.atlassianURL } }),
              ...(formData.atlassianUserEmail && { atlassianUserEmail: { value: formData.atlassianUserEmail } })
            }
          };

          const result = await deiterateAPI.POST('/deiterateInternalAPI/settings/createAPISecret', {
            body: JSON.stringify({
              customerID: activeTenancy.id,
              ...dataToSave
            }),
            companyID: activeTenancy.id
          });

          console.log('✅ Integration saved successfully (encrypted mode):', result);
        } catch (error) {
          console.error('❌ Error saving API key to secrets:', error);
          // Don't throw here - the Firestore save was successful

          // Show user-friendly message
          alert('Integration settings saved to local database, but there was an issue saving the API key securely. Please try again or contact support if the issue persists.');
        }
      }

      setIntegrations(updatedIntegrations);
      setConfluenceDialogOpen(false);
      console.log('Integration saved successfully!');
    } catch (error) {
      console.error('Error saving integration:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // SharePoint integration functions
  const addTestResult = (message: string) => {
    const timestamp = new Date().toLocaleTimeString();
    setTestResults(prev => [...prev, `[${timestamp}] ${message}`]);
  };

  const clearTestResults = () => {
    setTestResults([]);
  };

  const checkSharePointAuthStatus = async () => {
    try {
      setIsSharePointLoading(true);
      setSharePointError(null);
      addTestResult('Checking SharePoint authentication status...');

      const status = await documentsService.getSharePointAuthStatus();
      setSharePointAuthStatus(status);

      if (status.isAuthenticated) {
        addTestResult(`✅ Authenticated successfully`);
        addTestResult(`   Status: ${status.message || 'Connected'}`);
      } else {
        addTestResult('❌ Not authenticated');
      }
    } catch (error: any) {
      const errorMsg = `Error checking auth status: ${error.message}`;
      setSharePointError(errorMsg);
      addTestResult(`❌ ${errorMsg}`);
    } finally {
      setIsSharePointLoading(false);
    }
  };

  const authenticateSharePoint = async () => {
    try {
      if (!sharePointTenantId.trim()) {
        setSharePointError('Please enter a Tenant ID');
        return;
      }

      setIsSharePointLoading(true);
      setSharePointError(null);
      addTestResult(`Starting SharePoint authentication for tenant: ${sharePointTenantId}`);

      const authUrl = await documentsService.getSharePointAuthUrl(sharePointTenantId);
      addTestResult('✅ Generated auth URL, opening in new window...');

      // Open auth URL in new window
      const popup = window.open(authUrl, '_blank', 'width=600,height=700');

      addTestResult('Please complete authentication in the popup window...');

      // Listen for the popup to send back the authorization code
      const handleMessage = async (event: MessageEvent) => {
        if (event.data.type === 'sharepoint_auth_success') {
          try {
            addTestResult('✅ Authorization code received, exchanging for tokens...');

            // Exchange the authorization code for tokens
            await documentsService.handleSharePointCallback(event.data.code, sharePointTenantId);

            addTestResult('✅ SharePoint authentication successful!');

            // Check auth status to update UI
            await checkSharePointAuthStatus();

            // Clean up event listener
            window.removeEventListener('message', handleMessage);
          } catch (error: any) {
            const errorMsg = `Token exchange error: ${error.message}`;
            setSharePointError(errorMsg);
            addTestResult(`❌ ${errorMsg}`);
            window.removeEventListener('message', handleMessage);
          }
        }
      };

      // Add event listener for popup messages
      window.addEventListener('message', handleMessage);

      // Clean up if popup is closed manually
      const checkClosed = setInterval(() => {
        if (popup?.closed) {
          window.removeEventListener('message', handleMessage);
          clearInterval(checkClosed);
          addTestResult('Authentication popup was closed.');
        }
      }, 1000);
    } catch (error: any) {
      const errorMsg = `Authentication error: ${error.message}`;
      setSharePointError(errorMsg);
      addTestResult(`❌ ${errorMsg}`);
    } finally {
      setIsSharePointLoading(false);
    }
  };

  const loadSharePointSites = async () => {
    try {
      setIsSharePointLoading(true);
      setSharePointError(null);
      addTestResult('Loading SharePoint sites...');

      const sites = await documentsService.getSharePointSites();
      setSharePointSites(sites);
      addTestResult(`✅ Loaded ${sites.length} SharePoint sites`);

      sites.forEach(site => {
        addTestResult(`  - ${site.displayName} (${site.drives.length} drives)`);
      });
    } catch (error: any) {
      const errorMsg = `Error loading sites: ${error.message}`;
      setSharePointError(errorMsg);
      addTestResult(`❌ ${errorMsg}`);
    } finally {
      setIsSharePointLoading(false);
    }
  };

  const loadSharePointFiles = async (driveId: string, folderId?: string) => {
    try {
      setIsSharePointLoading(true);
      setSharePointError(null);
      addTestResult(`Loading files from drive: ${driveId}${folderId ? `, folder: ${folderId}` : ''}`);

      const files = await documentsService.getSharePointFiles(driveId, folderId);
      setSharePointFiles(files);
      addTestResult(`✅ Loaded ${files.length} files`);

      files.slice(0, 5).forEach(file => {
        addTestResult(`  - ${file.name} (${(file.size / 1024).toFixed(1)} KB)`);
      });

      if (files.length > 5) {
        addTestResult(`  ... and ${files.length - 5} more files`);
      }
    } catch (error: any) {
      const errorMsg = `Error loading files: ${error.message}`;
      setSharePointError(errorMsg);
      addTestResult(`❌ ${errorMsg}`);
    } finally {
      setIsSharePointLoading(false);
    }
  };

  const testPullFile = async (file: SharePointFile) => {
    try {
      setIsSharePointLoading(true);
      setSharePointError(null);
      addTestResult(`Pulling file: ${file.name}`);

      const result = await documentsService.pullSharePointFile(
        file.parentReference.driveId,
        file.id,
        {
          documentType: 'Document',
          classification: 'Business Confidential',
          // componentId: 'TEST-SP'
        }
      );

      if (result.success) {
        addTestResult(`✅ Successfully pulled file: ${result.fileName}`);
        addTestResult(`  Document ID: ${result.documentId}`);
        addTestResult(`  File size: ${result.fileSize} bytes`);
      } else {
        addTestResult(`❌ Failed to pull file: ${result.message}`);
      }
    } catch (error: any) {
      const errorMsg = `Error pulling file: ${error.message}`;
      setSharePointError(errorMsg);
      addTestResult(`❌ ${errorMsg}`);
    } finally {
      setIsSharePointLoading(false);
    }
  };

  return (
    <div className="space-y-10">
      <section className="overflow-hidden border border-white/10 bg-transparent">
        <div className="relative overflow-hidden bg-linear-to-br from-primary/15 via-background to-background px-10 pt-6 pb-10 text-foreground">
          <div className="pointer-events-none absolute inset-0 opacity-30 blur-3xl bg-[radial-gradient(circle_at_top,var(--tw-gradient-stops))] from-primary/40 via-transparent to-transparent" />
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute inset-y-0 right-0 w-full max-w-[840px] translate-x-12 sm:translate-x-8 lg:translate-x-5 xl:translate-x-3">
              <div
                className="absolute inset-0 bg-size-[75%] bg-top-right bg-no-repeat"
                style={{
                  backgroundImage: `url(${heroBackground as string})`,
                  opacity: isDark ? 0.2 : 0.35,
                  mixBlendMode: isDark ? 'screen' : 'soft-light',
                  filter: 'saturate(0.75)',
                  maskImage:
                    'linear-gradient(to right, transparent 0%, transparent 25%, rgba(0,0,0,0.65) 62%, black 85%)',
                  WebkitMaskImage:
                    'linear-gradient(to right, transparent 0%, transparent 25%, rgba(0,0,0,0.65) 62%, black 85%)',
                }}
              />
              <div className="absolute inset-0 bg-linear-to-r from-background/95 via-background/55 to-transparent" />
            </div>
          </div>
          <div className="relative flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
            <div className="max-w-2xl space-y-4">
              <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-1 text-sm font-medium text-primary">
                <Sparkles className="h-4 w-4" /> Integration Marketplace
              </div>
              <h2 className="text-3xl font-bold leading-tight lg:text-4xl">
                Curated integrations to power your controls, reviews, and automation
              </h2>
              <p className="text-base text-muted-foreground/90">
                Discover de.iterate hosted automations and self-hosted community integrations. Enable what you need per tenancy, configure credentials, and keep everything in sync without leaving settings.
              </p>
              <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
                <div className="inline-flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-3 py-2">
                  <ShieldCheck className="h-4 w-4 text-primary" />
                  Managed & self-hosted options
                </div>
                <div className="inline-flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-3 py-2">
                  <Server className="h-4 w-4 text-primary" />
                  Signed bundle downloads
                </div>
                <div className="inline-flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-3 py-2">
                  <Settings2 className="h-4 w-4 text-primary" />
                  Tenant-level configuration
                </div>
              </div>
              <div className="flex flex-wrap gap-3">
                <Button
                  variant="secondary"
                  className="border-white/30 bg-white/60 dark:bg-secondary text-primary shadow-lg shadow-primary/10 hover:bg-white/90"
                  onClick={() => setCatalogDialogOpen(true)}
                >
                  <Sparkles className="mr-2 h-4 w-4" />
                  Browse catalog
                </Button>
                <Button
                  variant="default"
                  className="border border-white/20 bg-primary text-primary-foreground shadow-lg shadow-primary/30"
                  onClick={refreshMarketplace}
                  disabled={marketplaceLoading}
                >
                  {marketplaceLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Refreshing…
                    </>
                  ) : (
                    <>
                      <Plug className="mr-2 h-4 w-4" />
                      Refresh catalog
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  className="border-white/30 bg-white/20 text-primary hover:bg-white/30"
                  onClick={() => window.open('https://docs.deiterate.com/integrations', '_blank')}
                >
                  Marketplace docs
                  <ExternalLink className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-10 border-t border-white/10 bg-card/95 px-8 py-8">
          <div className="space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <h3 className="text-2xl font-semibold text-foreground">Enabled Integrations</h3>
                <p className="text-sm text-muted-foreground">
                  Active automations for this tenancy. Browse the catalog to enable more.
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                {marketplaceError && (
                  <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-2 text-sm text-destructive">
                    {marketplaceError}
                  </div>
                )}
                <Button variant="outline" className="border-primary/30 text-primary hover:bg-primary/10" onClick={() => setCatalogDialogOpen(true)}>
                  Browse available integrations
                </Button>
              </div>
            </div>

            {marketplaceLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 4 }).map((_, idx) => (
                  <div key={idx} className="h-24 animate-pulse rounded-2xl border border-white/5 bg-white/5" />
                ))}
              </div>
            ) : enabledIntegrations.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-white/10 bg-white/5 px-6 py-12 text-center">
                <Plug className="mx-auto mb-4 h-10 w-10 text-primary" />
                <p className="text-lg font-medium text-foreground">No integrations enabled yet</p>
                <p className="text-sm text-muted-foreground">Open the catalog to explore available automations.</p>
                <Button className="mt-4 bg-primary text-primary-foreground" onClick={() => setCatalogDialogOpen(true)}>
                  Open marketplace
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {enabledIntegrations.map((integration) => {
                  const integrationRequiresConfig = Boolean(
                    integration.configSchema && Object.keys(integration.configSchema).length > 0
                  );
                  return (
                    <Collapsible key={integration.id} defaultOpen={false}>
                      <CollapsibleTrigger asChild>
                        <div className="group flex cursor-pointer items-center justify-between rounded-xl border border-white/10 bg-white/8 p-4 transition-all hover:border-primary/30 hover:bg-white/12">
                          <div className="flex items-center gap-4">
                            <div>
                              <h4 className="text-lg font-semibold text-slate-800 dark:text-foreground/95">{integration.name}</h4>
                              <p className="text-sm text-slate-600 dark:text-muted-foreground/90">
                                {integration.description || 'No description provided.'}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {integration.version && (
                              <span className="rounded-md border border-slate-300 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-700 dark:border-white/30 dark:bg-white/15 dark:text-foreground/90">
                                v{integration.version}
                              </span>
                            )}
                            <span className="rounded-md border border-emerald-200 bg-[rgba(56,161,105,0.1)] px-2.5 py-1 text-xs font-medium text-[#38a169] dark:border-emerald-500/60 dark:bg-emerald-500/25 dark:text-emerald-200">
                              Enabled
                            </span>
                            {integrationRequiresConfig && (
                              <span className="rounded-md border border-amber-200 bg-[rgba(246,150,29,0.1)] px-2.5 py-1 text-xs font-medium text-[#f6961d] dark:border-amber-500/60 dark:bg-amber-500/25 dark:text-amber-200">
                                Configurable
                              </span>
                            )}
                            <ChevronDown className="h-5 w-5 text-slate-500 dark:text-muted-foreground/80 transition-transform group-data-[state=open]:rotate-180" />
                          </div>
                        </div>
                      </CollapsibleTrigger>
                      <CollapsibleContent className="mt-2">
                        <Tabs 
                          value={integrationActiveTabs[integration.id] || "configuration"} 
                          onValueChange={(value) => setIntegrationActiveTabs(prev => ({ ...prev, [integration.id]: value }))}
                          className="w-full"
                        >
                          <TabsList className="grid w-full grid-cols-4">
                            <TabsTrigger value="configuration">Configuration</TabsTrigger>
                            <TabsTrigger value="actions">Actions</TabsTrigger>
                            <TabsTrigger value="history">History</TabsTrigger>
                            <TabsTrigger value="logs">Logs</TabsTrigger>
                          </TabsList>
                          
                          <TabsContent value="configuration" className="mt-4">
                            <div className="rounded-xl border border-white/5 bg-white/3 p-4 space-y-4">
                              {/* Meta info row */}
                              <div className="flex flex-wrap items-center gap-3 pb-4 text-xs">
                                <span className="rounded-md bg-slate-50 px-2 py-1 font-medium text-slate-700 dark:bg-muted/70 dark:text-muted-foreground/95">
                                  {getPublisherLabel(integration)}
                                </span>
                                <span className="rounded-md bg-slate-50 px-2 py-1 font-medium text-slate-700 dark:bg-muted/70 dark:text-muted-foreground/95">
                                  {getHostingLabel(integration)}
                                </span>
                                <span className="rounded-md bg-slate-50 px-2 py-1 font-medium text-slate-700 dark:bg-muted/70 dark:text-muted-foreground/95">
                                  {getTriggerSummary(integration)}
                                </span>
                                
                                {/* Entra Secret Expiry Status */}
                                {integration.id === 'entra-user-sync' && integration.tenantIntegration?.metadata?.secretExpiry && (
                                  <span className={`rounded-md px-2 py-1 font-medium ${
                                    integration.tenantIntegration.metadata.secretExpiry.isExpired
                                      ? 'border border-red-200 bg-red-50 text-red-700 dark:border-red-500/60 dark:bg-red-500/25 dark:text-red-200'
                                      : integration.tenantIntegration.metadata.secretExpiry.isExpiringSoon
                                        ? 'border border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/60 dark:bg-amber-500/25 dark:text-amber-200'
                                        : 'border border-slate-200 bg-slate-50 text-slate-700 dark:border-white/30 dark:bg-white/15 dark:text-foreground/90'
                                  }`}>
                                    {integration.tenantIntegration.metadata.secretExpiry.isExpired
                                      ? '🚨 Secret Expired'
                                      : integration.tenantIntegration.metadata.secretExpiry.isExpiringSoon
                                        ? `⚠️ Expires in ${integration.tenantIntegration.metadata.secretExpiry.daysUntilExpiry} days`
                                        : `Secret expires ${new Date(integration.tenantIntegration.metadata.secretExpiry.secretExpiryDate).toLocaleDateString()}`
                                    }
                                  </span>
                                )}
                                
                                {integration.definition?.documentationUrl && (
                                  <button
                                    type="button"
                                    className="ml-auto inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-700 dark:text-primary/90 dark:hover:text-primary hover:underline"
                                    onClick={() => window.open(integration.definition?.documentationUrl, '_blank')}
                                  >
                                    Documentation
                                    <ExternalLink className="h-3 w-3" />
                                  </button>
                                )}
                              </div>

                              {/* Actions */}
                              <div className="flex flex-wrap gap-2">
                                {/* Manual Trigger Button - All integrations support manual triggering */}
                                <Button
                                  variant="default"
                                  size="sm"
                                  className="bg-primary text-primary-foreground hover:bg-primary/90"
                                  disabled={actionIntegrationId === integration.id}
                                  onClick={() => handleManualTrigger(integration)}
                                >
                                  {actionIntegrationId === integration.id ? (
                                    <>
                                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                      Running...
                                    </>
                                  ) : (
                                    <>
                                      <Sparkles className="mr-2 h-4 w-4" />
                                      Run Now
                                    </>
                                  )}
                                </Button>
                                <Button
                                  variant="secondary"
                                  size="sm"
                                  className="border border-destructive/30 bg-destructive/15 text-destructive hover:bg-destructive/25"
                                  disabled={actionIntegrationId === integration.id}
                                  onClick={() => handleDisableIntegration(integration.id)}
                                >
                                  {actionIntegrationId === integration.id ? (
                                    <>
                                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                      Disabling...
                                    </>
                                  ) : (
                                    <>
                                      <Power className="mr-2 h-4 w-4" />
                                      Disable
                                    </>
                                  )}
                                </Button>
                                {integrationRequiresConfig && (
                                  <Button
                                    variant="secondary"
                                    size="sm"
                                    disabled={actionIntegrationId === integration.id}
                                    onClick={() => handleConfigureIntegration(integration)}
                                  >
                                    {actionIntegrationId === integration.id ? (
                                      <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Loading...
                                      </>
                                    ) : (
                                      <>
                                        <Settings className="mr-2 h-4 w-4" />
                                        Configure
                                      </>
                                    )}
                                  </Button>
                                )}
                                {integration.definition?.documentationUrl && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => window.open(integration.definition?.documentationUrl, '_blank')}
                                  >
                                    <BookOpen className="mr-2 h-4 w-4" />
                                    Docs
                                  </Button>
                                )}
                              </div>
                            </div>
                          </TabsContent>
                          
                          <TabsContent value="actions" className="mt-4">
                            <div className="rounded-xl border border-white/5 bg-white/3 p-4 space-y-4">
                              <div className="mb-4">
                                <h4 className="text-sm font-semibold text-foreground mb-2">Available Actions</h4>
                                <p className="text-xs text-muted-foreground">
                                  Execute integration actions manually. Each action may require specific parameters.
                                </p>
                              </div>
                              
                              {integration.configSchema?.actions || integration.definition?.actions ? (
                                <div className="space-y-3">
                                  {Object.entries(integration.configSchema?.actions || integration.definition?.actions || {}).map(([actionKey, actionDef]: [string, any]) => (
                                    <div key={actionKey} className="rounded-lg border border-white/10 bg-white/5 p-4">
                                      <div className="flex items-start justify-between mb-3">
                                        <div className="flex-1">
                                          <h5 className="font-medium text-foreground capitalize">{actionKey.replace(/([A-Z])/g, ' $1').trim()}</h5>
                                          {actionDef.description && (
                                            <p className="text-xs text-muted-foreground mt-1">{actionDef.description}</p>
                                          )}
                                        </div>
                                        <Button
                                          size="sm"
                                          variant="default"
                                          disabled={actionIntegrationId === integration.id}
                                          onClick={async () => {
                                            if (!tenancyId) {
                                              toast.error('No tenancy ID available');
                                              return;
                                            }
                                            setActionIntegrationId(integration.id);
                                            try {
                                              const response = await deiterateAPI.POST(
                                                `/v1/integrations/${integration.id}/actions/${actionKey}`,
                                                {
                                                  backend: 'thalassa',
                                                  headers: {
                                                    'x-tenancy-id': tenancyId,
                                                    'company-id': tenancyId,
                                                    'customer-id': tenancyId,
                                                  },
                                                  body: {
                                                    params: {},
                                                  },
                                                }
                                              );
                                              
                                              if (response?.statusCode === 200) {
                                                toast.success(`Action "${actionKey}" executed successfully`);
                                                // Show result if available
                                                if (response?.result) {
                                                  console.log(`[${actionKey}] Result:`, response.result);
                                                }
                                              } else {
                                                throw new Error(response?.error || 'Failed to execute action');
                                              }
                                            } catch (error: any) {
                                              console.error(`[IntegrationsSection] Failed to execute action ${actionKey}`, error);
                                              toast.error(error?.message || `Failed to execute action "${actionKey}"`);
                                            } finally {
                                              setActionIntegrationId(null);
                                            }
                                          }}
                                        >
                                          {actionIntegrationId === integration.id ? (
                                            <>
                                              <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                                              Running...
                                            </>
                                          ) : (
                                            <>
                                              <Sparkles className="mr-2 h-3 w-3" />
                                              Run
                                            </>
                                          )}
                                        </Button>
                                      </div>
                                      
                                      {/* Show parameters if any */}
                                      {actionDef.params && Object.keys(actionDef.params).length > 0 && (
                                        <div className="mt-2 pt-2 border-t border-white/10">
                                          <p className="text-xs text-muted-foreground mb-1">Parameters:</p>
                                          <div className="space-y-1">
                                            {Object.entries(actionDef.params).map(([paramKey, paramDef]: [string, any]) => (
                                              <div key={paramKey} className="text-xs">
                                                <span className="font-mono text-primary">{paramKey}</span>
                                                {paramDef.required && <span className="text-red-500 ml-1">*</span>}
                                                {paramDef.description && (
                                                  <span className="text-muted-foreground ml-2">- {paramDef.description}</span>
                                                )}
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                      
                                      {/* Show return types if any */}
                                      {actionDef.returns && Object.keys(actionDef.returns).length > 0 && (
                                        <div className="mt-2 pt-2 border-t border-white/10">
                                          <p className="text-xs text-muted-foreground mb-1">Returns:</p>
                                          <div className="space-y-1">
                                            {Object.entries(actionDef.returns).map(([returnKey, returnDef]: [string, any]) => (
                                              <div key={returnKey} className="text-xs">
                                                <span className="font-mono text-emerald-600 dark:text-emerald-400">{returnKey}</span>
                                                {returnDef.description && (
                                                  <span className="text-muted-foreground ml-2">- {returnDef.description}</span>
                                                )}
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="rounded-lg border border-dashed border-white/10 bg-white/5 p-8 text-center">
                                  <Info className="mx-auto mb-2 h-8 w-8 text-muted-foreground" />
                                  <p className="text-sm text-muted-foreground">
                                    No actions defined for this integration
                                  </p>
                                </div>
                              )}
                            </div>
                          </TabsContent>
                          
                          <TabsContent value="history" className="mt-4">
                            <ExecutionHistory 
                              integrationId={integration.id}
                              onViewLogs={(eventId) => {
                                setSelectedLogEvents(prev => ({ ...prev, [integration.id]: eventId }));
                                setIntegrationActiveTabs(prev => ({ ...prev, [integration.id]: 'logs' }));
                              }}
                            />
                          </TabsContent>
                          
                          <TabsContent value="logs" className="mt-4">
                            {selectedLogEvents[integration.id] ? (
                              <div className="space-y-4">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setSelectedLogEvents(prev => ({ ...prev, [integration.id]: '' }))}
                                >
                                  ← Back to History
                                </Button>
                                <LogViewer
                                  eventId={selectedLogEvents[integration.id]}
                                  onClose={() => setSelectedLogEvents(prev => ({ ...prev, [integration.id]: '' }))}
                                />
                              </div>
                            ) : (
                              <div className="rounded-xl border border-white/5 bg-white/3 p-8">
                                <p className="text-center text-muted-foreground">
                                  Select an execution from the History tab to view its logs
                                </p>
                              </div>
                            )}
                          </TabsContent>
                        </Tabs>
                      </CollapsibleContent>
                    </Collapsible>
                  );
                })}
              </div>
            )}

            <div className="space-y-6 border-t border-white/10 pt-6">
            {/* Legacy Integrations Section */}
             <div className="flex items-center gap-3">
              <div>
                <h3 className="text-2xl font-semibold text-foreground">Legacy Connections</h3>
                <p className="text-sm text-muted-foreground">
                  These integrations pre-date the marketplace and rely on direct credential storage. Prefer managed marketplace integrations when possible.
                </p>
              </div>
            </div>
            <Accordion type="multiple" className="space-y-4">
              <AccordionItem value="legacy-confluence" className="rounded-2xl border border-white/10 bg-white/5 px-4">
                <AccordionTrigger className="text-left text-base font-semibold text-foreground">
                  <div className="flex flex-col gap-1 text-left">
                    <span>Confluence (Direct Credentials)</span>
                    <span className="text-sm font-normal text-muted-foreground">
                      Stores API token + integration user email for legacy ingestion flows.
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <p>Existing values are stored per tenancy in Firestore + Secrets Manager.</p>
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" variant="secondary" className="bg-primary/10 text-primary hover:bg-primary/20" onClick={() => setConfluenceDialogOpen(true)}>
                        Configure credentials
                      </Button>
                      <Button size="sm" variant="ghost" asChild>
                        <a
                          href="https://support.atlassian.com/atlassian-account/docs/manage-api-tokens-for-your-atlassian-account/"
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-2"
                        >
                          Token guide
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      </Button>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
              {hasSharePoint && (
                <AccordionItem value="legacy-sharepoint" className="rounded-2xl border border-white/10 bg-white/5 px-4">
                  <AccordionTrigger className="text-left text-base font-semibold text-foreground">
                    <div className="flex flex-col gap-1 text-left">
                      <span>SharePoint OAuth Sandbox</span>
                      <span className="text-sm font-normal text-muted-foreground">
                        Manual OAuth + browsing experience for validating SharePoint connections.
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-muted-foreground">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="flex flex-col gap-1 text-sm text-muted-foreground">
                        <span>
                          {sharePointAuthStatus.isAuthenticated
                            ? `Authenticated as ${sharePointAuthStatus.sharePointUser?.displayName ?? 'unknown user'}.`
                            : 'Not authenticated. Launch the sandbox to test OAuth flows and ingestion.'}
                        </span>
                        {sharePointAuthStatus.tenant?.domain && (
                          <span className="text-xs text-muted-foreground/80">
                            Tenant: {sharePointAuthStatus.tenant?.domain}
                          </span>
                        )}
                      </div>
                      <Button size="sm" variant="secondary" className="bg-primary/10 text-primary hover:bg-primary/20" onClick={() => setSharePointDialogOpen(true)}>
                        Open sandbox
                      </Button>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              )}
            </Accordion>
            </div>
          </div>
        </div>
      </section>

      <Dialog open={configModalOpen} onOpenChange={(open) => (!open ? closeConfigModal() : setConfigModalOpen(true))}>
        <DialogContent className="max-w-6xl! bg-card">
          <DialogHeader>
            <DialogTitle>Configure {selectedIntegration?.name}</DialogTitle>
            <DialogDescription>
              Provide tenant-specific configuration for this integration. This will be stored securely in Firestore.
            </DialogDescription>
          </DialogHeader>
          
          {/* Tabs for Configuration and Setup */}
          <Tabs defaultValue="config" className="w-full mt-6">
            <TabsList className={`grid ${setupInstructions ? 'grid-cols-2' : 'grid-cols-1'}`}>
              <TabsTrigger value="config">Configuration</TabsTrigger>
              {setupInstructions && (
                <TabsTrigger value="setup">Setup Instructions</TabsTrigger>
              )}
            </TabsList>
            
            <TabsContent value="config" className="mt-6">
                {(() => {
                  console.log('[RENDER] configUsesSchema:', configUsesSchema);
                  console.log('[RENDER] configSchemaFields:', configSchemaFields);
                  console.log('[RENDER] configSchemaFields.length:', configSchemaFields.length);
                  return null;
                })()}
                {configUsesSchema ? (
            <div className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {configSchemaFields.map((field) => (
                  <div
                    key={field.key}
                    className={cn(
                      "space-y-2 rounded-2xl border border-white/10 bg-white/5 p-4",
                      field.type === "array" && "md:col-span-2 xl:col-span-3"
                    )}
                  >
                    <div className="flex flex-col gap-1">
                      <Label htmlFor={`config-${field.key}`} className="text-sm font-medium text-foreground">
                        {field.label}
                        {field.required && <span className="ml-1 text-destructive">*</span>}
                      </Label>
                      {field.description && (
                        <span className="text-xs text-muted-foreground">{field.description}</span>
                      )}
                    </div>
                    {field.enum ? (
                      <Select
                        value={getFieldDisplayValue(field) || ''}
                        onValueChange={(value) => setFieldValue(field, value)}
                      >
                        <SelectTrigger id={`config-${field.key}`} className="bg-background/80">
                          <SelectValue placeholder="Select value" />
                        </SelectTrigger>
                        <SelectContent>
                          {field.enum.map((option) => (
                            <SelectItem key={option} value={option}>
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : field.type === 'boolean' ? (
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id={`config-${field.key}`}
                          checked={Boolean(getFieldDisplayValue(field))}
                          onCheckedChange={(checked) => setFieldValue(field, checked)}
                        />
                        <Label htmlFor={`config-${field.key}`} className="text-sm text-muted-foreground">
                          Enable
                        </Label>
                      </div>
                    ) : field.type === 'array' ? (
                      <Textarea
                        id={`config-${field.key}`}
                        value={getFieldDisplayValue(field)}
                        onChange={(e) => setFieldValue(field, e.target.value)}
                        className="min-h-[120px] font-mono text-sm"
                        placeholder="Enter values separated by new lines"
                      />
                    ) : (
                      <Input
                        id={`config-${field.key}`}
                        type={field.type === 'number' || field.type === 'integer' ? 'number' : field.format === 'password' ? 'password' : 'text'}
                        value={getFieldDisplayValue(field)}
                        onChange={(e) => setFieldValue(field, e.target.value)}
                        className="bg-background/80"
                        placeholder={field.format === 'uri' ? 'https://example.com' : field.format === 'email' ? 'user@example.com' : ''}
                      />
                    )}
                  </div>
                ))}
              </div>
              {configSchemaFields.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  No configurable fields defined in this integration's schema.
                </p>
              )}
              {configError && <p className="text-sm text-destructive">{configError}</p>}
            </div>
          ) : (
            <div className="space-y-4">
              <Label className="text-sm font-medium text-foreground">Configuration JSON</Label>
              <Textarea
                value={configTextareaValue}
                onChange={(e) => setConfigTextareaValue(e.target.value)}
                className="min-h-[240px] font-mono text-sm"
              />
              {configError && <p className="text-sm text-destructive">{configError}</p>}
            </div>
          )}
              </TabsContent>
              
              <TabsContent value="setup" className="mt-6 space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Steps Column */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 mb-4">
                      <BookOpen className="h-5 w-5 text-primary" />
                      <h4 className="text-lg font-semibold">Setup Steps</h4>
                    </div>
                    <ol className="space-y-3">
                      {setupInstructions?.steps?.map((step: string, index: number) => (
                        <li key={index} className="flex gap-3">
                          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-medium text-primary-foreground">
                            {index + 1}
                          </span>
                          <p className="text-sm leading-relaxed pt-1">{step}</p>
                        </li>
                      ))}
                    </ol>
                    
                    {setupInstructions?.documentation && (
                      <div className="pt-4">
                        <a
                          href={setupInstructions.documentation}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
                        >
                          <ExternalLink className="h-4 w-4" />
                          View Official Documentation
                        </a>
                      </div>
                    )}
                  </div>
                  
                  {/* Permissions Column */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 mb-4">
                      <ShieldCheck className="h-5 w-5 text-primary" />
                      <h4 className="text-lg font-semibold">Required Permissions</h4>
                    </div>
                    
                    {selectedIntegration?.configSchema?.requiredPermissions && 
                     selectedIntegration.configSchema.requiredPermissions.length > 0 && (
                      <div className="space-y-3">
                        {selectedIntegration.configSchema.requiredPermissions.map((perm: any, permIndex: number) => (
                          <div key={permIndex} className="rounded-lg border bg-card p-4">
                            <code className="text-sm font-mono font-medium text-primary">{perm.permission}</code>
                            <p className="text-sm text-foreground mt-2">{perm.description}</p>
                            <p className="text-xs text-muted-foreground mt-1">Type: {perm.type}</p>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {setupInstructions?.permissionsNote && (
                      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800/30 dark:bg-amber-950/20">
                        <div className="flex gap-3">
                          <ShieldCheck className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0" />
                          <div>
                            <p className="text-sm font-medium text-amber-800 dark:text-amber-200">Important Note</p>
                            <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                              {setupInstructions?.permissionsNote}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>
              {setupInstructions && (
                <TabsContent value="setup" className="mt-6">
                  {/* Setup Instructions Content */}
                  <div className="space-y-4">
                    {setupInstructions?.steps && (
                      <div className="space-y-3">
                        <h3 className="text-lg font-semibold">Setup Steps</h3>
                        <ol className="list-decimal list-inside space-y-2">
                          {setupInstructions?.steps?.map((step: string, index: number) => (
                            <li key={index} className="text-sm">{step}</li>
                          ))}
                        </ol>
                      </div>
                    )}
                    
                    {setupInstructions?.documentation && (
                      <div className="space-y-3">
                        <h3 className="text-lg font-semibold">Documentation</h3>
                        <a 
                          href={setupInstructions?.documentation} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 hover:underline"
                        >
                          View Documentation
                        </a>
                      </div>
                    )}
                    
                    {selectedIntegration?.configSchema?.requiredPermissions && 
                     selectedIntegration.configSchema.requiredPermissions.length > 0 && (
                      <div className="space-y-4">
                        <h4 className="text-lg font-semibold">Required Permissions</h4>
                        <div className="space-y-3">
                          {selectedIntegration.configSchema.requiredPermissions.map((perm: any, permIndex: number) => (
                            <div key={permIndex} className="rounded-lg border bg-card p-4">
                              <code className="text-sm font-mono font-medium text-primary">{perm.permission}</code>
                              <p className="text-sm text-foreground mt-2">{perm.description}</p>
                              <p className="text-xs text-muted-foreground mt-1">Type: {perm.type}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {setupInstructions?.permissionsNote && (
                      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800/30 dark:bg-amber-950/20">
                        <div className="flex gap-3">
                          <ShieldCheck className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0" />
                          <div>
                            <p className="text-sm font-medium text-amber-800 dark:text-amber-200">Important Note</p>
                            <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                              {setupInstructions?.permissionsNote}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </TabsContent>
              )}
            </Tabs>
          
          <DialogFooter className="mt-6">
            <Button variant="ghost" onClick={closeConfigModal} disabled={configSaving}>
              Cancel
            </Button>
            <Button onClick={handleSaveConfig} disabled={configSaving}>
              {configSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save config & enable'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={confluenceDialogOpen} onOpenChange={setConfluenceDialogOpen}>
        <DialogContent className="max-w-3xl bg-card">
          <DialogHeader>
            <DialogTitle>Legacy Confluence credentials</DialogTitle>
            <DialogDescription>
              Store the API token, integration user email, and site URL used by the legacy Confluence ingestion flow.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-6 md:grid-cols-2">
            <div className="space-y-4 rounded-2xl border border-white/10 bg-white/5 p-5">
              <label htmlFor="atlassianAPIKeyInput" className="block text-sm font-medium text-foreground">
                Confluence API Key
              </label>
              <Input
                type="password"
                id="atlassianAPIKeyInput"
                value={formData.atlassianAPIKey}
                onChange={(e) => handleInputChange('atlassianAPIKey', e.target.value)}
                disabled={isLoading}
                placeholder={isLoading ? "Loading..." : "Enter Confluence API key"}
              />
              <p className="text-xs text-muted-foreground">
                Generate tokens from the{" "}
                <a
                  href="https://support.atlassian.com/atlassian-account/docs/manage-api-tokens-for-your-atlassian-account/"
                  className="text-primary underline-offset-2 hover:underline"
                  target="_blank"
                  rel="noreferrer"
                >
                  Atlassian account portal
                </a>.
              </p>
            </div>
            <div className="space-y-4">
              <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
                <label htmlFor="atlassianURLInput" className="block text-sm font-medium text-foreground">
                  Confluence URL
                </label>
                <Input
                  type="text"
                  id="atlassianURLInput"
                  value={formData.atlassianURL}
                  onChange={(e) => handleInputChange('atlassianURL', e.target.value)}
                  disabled={isLoading}
                  placeholder="deiterate.atlassian.net"
                />
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
                <label htmlFor="atlassianUserEmailInput" className="block text-sm font-medium text-foreground">
                  Confluence Email
                </label>
                <Input
                  type="email"
                  id="atlassianUserEmailInput"
                  value={formData.atlassianUserEmail}
                  onChange={(e) => handleInputChange('atlassianUserEmail', e.target.value)}
                  disabled={isLoading}
                  placeholder="you@company.com"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfluenceDialogOpen(false)} disabled={isSaving}>
              Cancel
            </Button>
            <Button onClick={handleLegacyConfluenceSave} disabled={isLoading || isSaving}>
              {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save credentials'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

  {
    hasSharePoint && (
      <Dialog open={sharePointDialogOpen} onOpenChange={setSharePointDialogOpen}>
        <DialogContent className="max-w-[90vw] xl:max-w-5xl bg-card">
          <DialogHeader>
            <DialogTitle>SharePoint legacy sandbox</DialogTitle>
            <DialogDescription>
              Use this sandbox to authenticate against SharePoint, browse sites/drives, and test file pulls before migrating to managed integrations.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6">
            <div className="grid gap-6 md:grid-cols-2">
              <div className="space-y-2">
                <label htmlFor="sharePointTenantId" className="block text-sm font-medium text-foreground">
                  SharePoint Tenant ID
                </label>
                <Input
                  type="text"
                  id="sharePointTenantId"
                  value={sharePointTenantId}
                  onChange={(e) => setSharePointTenantId(e.target.value)}
                  placeholder="yourtenant.onmicrosoft.com"
                />
              </div>
              <div className="grid gap-3 text-sm text-muted-foreground">
                <span>
                  Status:{" "}
                  {sharePointAuthStatus.isAuthenticated
                    ? `Connected as ${sharePointAuthStatus.sharePointUser?.displayName ?? 'unknown user'}`
                    : 'Not authenticated'}
                </span>
                {sharePointAuthStatus.tenant?.domain && (
                  <span className="text-xs">Tenant: {sharePointAuthStatus.tenant?.domain}</span>
                )}
              </div>
            </div>
            <div className="space-y-4 rounded-xl border border-white/10 bg-background/60 p-4">
              <h4 className="font-medium text-foreground">1. Authentication</h4>
              <div className="flex flex-wrap gap-2">
                <Button variant="secondary" onClick={checkSharePointAuthStatus} disabled={isSharePointLoading}>
                  Check status
                </Button>
                <Button
                  variant="default"
                  onClick={authenticateSharePoint}
                  disabled={isSharePointLoading || !sharePointTenantId.trim()}
                >
                  Authenticate
                </Button>
              </div>
              {sharePointAuthStatus.isAuthenticated && (
                <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-4 text-sm text-emerald-200">
                  <p className="font-semibold text-emerald-100">
                    Connected as {sharePointAuthStatus.sharePointUser?.displayName}
                  </p>
                  <p className="text-xs">Tenant: {sharePointAuthStatus.tenant?.domain}</p>
                </div>
              )}
            </div>
            {sharePointAuthStatus.isAuthenticated && (
              <>
                <div className="space-y-4 rounded-xl border border-white/10 bg-background/60 p-4">
                  <h4 className="font-medium text-foreground">2. Browse SharePoint Sites</h4>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={loadSharePointSites} disabled={isSharePointLoading}>
                      Load sites
                    </Button>
                  </div>
                  {sharePointSites.length > 0 && (
                    <select
                      value={selectedSite?.id || ''}
                      onChange={(e) => {
                        const site = sharePointSites.find((s) => s.id === e.target.value);
                        setSelectedSite(site || null);
                        setSelectedDrive(null);
                        setSharePointFiles([]);
                      }}
                      className="w-full rounded-lg border border-white/10 bg-background/80 px-3 py-2 text-sm text-foreground"
                    >
                      <option value="">Select a site…</option>
                      {sharePointSites.map((site) => (
                        <option key={site.id} value={site.id}>
                          {site.displayName} ({site.drives.length} drives)
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                {selectedSite && (
                  <div className="space-y-4 rounded-xl border border-white/10 bg-background/60 p-4">
                    <h4 className="font-medium text-foreground">3. Browse Drives & Files</h4>
                    {selectedSite.drives.length > 0 && (
                      <select
                        value={selectedDrive?.id || ''}
                        onChange={(e) => {
                          const drive = selectedSite.drives.find((d) => d.id === e.target.value);
                          setSelectedDrive(drive || null);
                          setSharePointFiles([]);
                        }}
                        className="w-full rounded-lg border border-white/10 bg-background/80 px-3 py-2 text-sm text-foreground"
                      >
                        <option value="">Select a drive…</option>
                        {selectedSite.drives.map((drive) => (
                          <option key={drive.id} value={drive.id}>
                            {drive.name} ({drive.driveType})
                          </option>
                        ))}
                      </select>
                    )}
                    {selectedDrive && (
                      <>
                        <Button
                          variant="outline"
                          onClick={() => loadSharePointFiles(selectedDrive.id)}
                          disabled={isSharePointLoading}
                        >
                          Load files
                        </Button>
                        <div className="max-h-48 space-y-2 overflow-y-auto rounded-lg border border-white/10 bg-background/50 p-3 text-sm">
                          {sharePointFiles.length === 0 && <p className="text-muted-foreground">No files loaded yet.</p>}
                          {sharePointFiles.map((file) => (
                            <div
                              key={file.id}
                              className="flex items-center justify-between rounded border border-white/10 bg-white/5 px-3 py-2"
                            >
                              <div>
                                <p className="font-medium text-foreground">{file.name}</p>
                                <p className="text-xs text-muted-foreground">
                                  {(file.size / 1024).toFixed(1)} KB • {new Date(file.lastModifiedDateTime).toLocaleDateString()}
                                </p>
                              </div>
                              <Button variant="ghost" size="sm" onClick={() => testPullFile(file)} disabled={isSharePointLoading}>
                                Pull
                              </Button>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </>
            )}
            <div className="space-y-4 rounded-xl border border-white/10 bg-background/60 p-4">
              <div className="flex items-center justify-between">
                <h4 className="font-medium text-foreground">Test results</h4>
                <Button variant="ghost" size="sm" onClick={clearTestResults}>
                  Clear
                </Button>
              </div>
              {sharePointError && (
                <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {sharePointError}
                </div>
              )}
              <div className="max-h-64 space-y-1 overflow-y-auto rounded-lg border border-white/10 bg-background/70 p-3 text-xs font-mono">
                {testResults.length === 0 ? (
                  <p className="text-muted-foreground">No test results yet. Start by checking authentication status.</p>
                ) : (
                  testResults.map((result, index) => <p key={index}>{result}</p>)
                )}
              </div>
              {isSharePointLoading && (
                <div className="flex items-center gap-2 text-primary">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-sm">Loading…</span>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSharePointDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }

  <Dialog open={catalogDialogOpen} onOpenChange={setCatalogDialogOpen}>
    <DialogContent className="max-w-[90vw] xl:max-w-7xl border border-white/10 bg-card p-0">
      <div className="flex flex-col gap-6 border-b border-white/10 bg-linear-to-br from-background via-card/90 to-card/70 px-8 py-6">
        <DialogHeader className="text-left">
          <DialogTitle className="text-3xl font-semibold text-foreground">
            Integration Catalog
          </DialogTitle>
          <DialogDescription className="text-base text-muted-foreground">
            Search, filter, and enable integrations from the marketplace. Newly enabled integrations will appear in the list above.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <Input
            placeholder="Search integrations…"
            value={catalogSearch}
            onChange={(e) => setCatalogSearch(e.target.value)}
            className="h-11 rounded-2xl border-white/20 bg-background/80 text-foreground placeholder:text-muted-foreground lg:max-w-md"
          />
          <div className="flex flex-wrap gap-2 text-sm">
            {(['all', 'hosted', 'self'] as const).map((filter) => (
              <Button
                key={filter}
                variant={catalogHostingFilter === filter ? 'default' : 'outline'}
                className={`rounded-full border ${catalogHostingFilter === filter ? 'bg-primary text-primary-foreground' : 'border-white/20 bg-white/5 text-muted-foreground hover:bg-white/10'}`}
                onClick={() =>
                  setCatalogHostingFilter((prev) =>
                    prev === filter ? 'all' : filter
                  )
                }
              >
                {filter === 'all' ? 'All hosting' : filter === 'hosted' ? 'Hosted' : 'Self-managed'}
              </Button>
            ))}
            {(['all', 'enabled', 'available'] as const).map((filter) => (
              <Button
                key={filter}
                variant={catalogStatusFilter === filter ? 'default' : 'outline'}
                className={`rounded-full border ${catalogStatusFilter === filter ? 'bg-primary text-primary-foreground' : 'border-white/20 bg-white/5 text-muted-foreground hover:bg-white/10'}`}
                onClick={() =>
                  setCatalogStatusFilter((prev) =>
                    prev === filter ? 'all' : filter
                  )
                }
              >
                {filter === 'all' ? 'All statuses' : filter === 'enabled' ? 'Enabled' : 'Available'}
              </Button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-h-[75vh] overflow-y-auto px-8 py-6">
        {marketplaceLoading ? (
          <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
            {Array.from({ length: 6 }).map((_, idx) => (
              <div key={idx} className="h-64 animate-pulse rounded-2xl border border-white/5 bg-white/5" />
            ))}
          </div>
        ) : filteredCatalogIntegrations.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-white/10 bg-white/5 px-6 py-16 text-center">
            <Sparkles className="mx-auto mb-4 h-10 w-10 text-primary" />
            <p className="text-lg font-medium text-foreground">No integrations match your filters</p>
            <p className="text-sm text-muted-foreground">Try adjusting your search or filter selections.</p>
          </div>
        ) : (
          <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
            {filteredCatalogIntegrations.map((integration) => {
              const integrationRequiresConfig = Boolean(
                integration.configSchema && Object.keys(integration.configSchema).length > 0
              );
              const hostingLabel = getHostingLabel(integration);
              const publisher = getPublisherLabel(integration);
              const isProcessing = actionIntegrationId === integration.id;
              const metaRows = [
                { label: 'Publisher', value: publisher, icon: Sparkles },
                { label: 'Hosted by', value: hostingLabel, icon: Server },
              ];
              return (
                <div
                  key={integration.id}
                  className="group flex h-full flex-col rounded-[30px] border border-white/10 bg-card/95 p-6 shadow-xl shadow-black/5 transition hover:-translate-y-1 hover:border-primary/40"
                >
                  <div className="flex items-start justify-between gap-4 border-b border-muted pb-4">
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-[11px] font-semibold uppercase text-muted-foreground">
                        <span
                          className={cn(
                            'inline-flex items-center gap-1 rounded-full border px-3 py-1 tracking-[0.2em]',
                            integration.enabled
                              ? 'border-emerald-300/60 bg-emerald-50 text-emerald-700'
                              : 'border-primary/30 bg-primary/5 text-primary'
                          )}
                        >
                          {integration.enabled ? 'Enabled' : 'Available'}
                        </span>
                        {integration.version && (
                          <span className="inline-flex items-center gap-1 rounded-full border border-muted px-3 py-1 tracking-[0.15em] text-[11px] uppercase text-muted-foreground/80">
                            v{integration.version}
                          </span>
                        )}
                      </div>
                      <h4 className="text-2xl font-semibold text-foreground">{integration.name}</h4>
                      <p className="text-sm text-muted-foreground">
                        {integration.description || 'No description provided.'}
                      </p>
                    </div>
                    <div className="rounded-2xl border border-muted bg-muted/30 p-3 text-primary">
                      <Plug className="h-5 w-5" />
                    </div>
                  </div>

                  <div className="mt-5 grid gap-2 text-sm">
                    {metaRows.map(({ label, value, icon: Icon }) => (
                      <div
                        key={label}
                        className="flex items-center justify-between rounded-2xl border border-muted bg-muted/30 px-4 py-3 text-foreground"
                      >
                        <span className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground/80">
                          <Icon className="h-4 w-4 text-primary" />
                          {label}
                        </span>
                        <span className="font-semibold">{value}</span>
                      </div>
                    ))}
                  </div>

                  <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                    {integration.definition?.longDescription ||
                      'Keeps assurance reviews on cadence by opening and updating Jira tickets for due or overdue controls.'}
                  </p>

                  <div className="mt-4 flex flex-wrap gap-2 text-xs text-muted-foreground">
                    <span className="rounded-2xl border border-muted bg-muted/30 px-4 py-2 text-foreground">
                      {getTriggerSummary(integration)}
                    </span>
                    {integrationRequiresConfig && (
                      <span className="rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-primary">
                        Configuration required
                      </span>
                    )}
                    {integration.definition?.documentationUrl && (
                      <button
                        type="button"
                        onClick={() => window.open(integration.definition?.documentationUrl, "_blank")}
                        className="inline-flex items-center gap-1 rounded-full border border-muted bg-muted/20 px-3 py-1 text-primary hover:bg-muted/40"
                      >
                        Docs
                        <ExternalLink className="h-3 w-3" />
                      </button>
                    )}
                  </div>

                  <div className="mt-auto border-t border-muted pt-4">
                    <div className="flex flex-wrap gap-2">
                      {integration.enabled ? (
                        <>
                          <Button
                            variant="secondary"
                            className="flex-1 border border-destructive/20 bg-destructive/10 text-destructive hover:bg-destructive/20"
                            disabled={isProcessing}
                            onClick={() => handleDisableIntegration(integration.id)}
                          >
                            {isProcessing ? <Loader2 className="h-4 w-4 animate-spin" /> : "Disable"}
                          </Button>
                          {integrationRequiresConfig && (
                            <Button
                              variant="outline"
                              className="flex-1 border border-primary/30 text-primary hover:bg-primary/10"
                              onClick={() => openConfigModal(integration)}
                            >
                              Configure
                            </Button>
                          )}
                        </>
                      ) : (
                        <Button
                          variant="default"
                          className="flex-1 rounded-2xl bg-primary text-primary-foreground shadow-lg shadow-primary/30"
                          disabled={isProcessing}
                          onClick={() =>
                            integrationRequiresConfig
                              ? openConfigModal(integration)
                              : handleEnableIntegration(integration.id)
                          }
                        >
                          {isProcessing ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : integrationRequiresConfig ? (
                            "Configure & Enable"
                          ) : (
                            "Enable"
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </DialogContent>
  </Dialog>
    </div>
  );
}
